import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUserShield,
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaCheckCircle,
  FaExclamationCircle,
  FaSpinner,
} from "react-icons/fa";
import "../styles/login.css";
import { API_ENDPOINTS } from "../config/api";

const Login = () => {
  const navigate = useNavigate();

  /* ================= STATES ================= */
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null); // { type: 'success' | 'error', message, title }

  const showNotice = (type, message, title = "") => {
    setNotification({ type, message, title });
  };

  /* ================= LOGIN FUNCTION ================= */
  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email.trim() || !password) {
      showNotice("error", "Please enter both email and password.", "Missing Fields");
      return;
    }

    try {
      setLoading(true);
      setNotification(null);

      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          password,
        }),
      });

      const data = await response.json();

      /* ================= SUCCESS ================= */
      if (response.ok) {
        localStorage.setItem("adminToken", data.token);
        localStorage.setItem("admin", JSON.stringify(data.admin));

        showNotice("success", "Welcome back! Redirecting to Admin Dashboard...", "Login Successful");

        setTimeout(() => {
          navigate("/admin/dashboard");
        }, 1100);
      } else {
        showNotice("error", data.message || "Invalid email or password. Please try again.", "Authentication Failed");
      }
    } catch (error) {
      console.error("Login Error:", error);
      showNotice("error", "Server connection error. Please verify backend is active.", "Server Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* ================= FLOATING TOAST NOTIFICATION ================= */}
      {notification && (
        <div className={`login-toast-banner ${notification.type}`} role="alert">
          <div className="login-toast-icon">
            {notification.type === "success" ? (
              <FaCheckCircle className="icon-success" />
            ) : (
              <FaExclamationCircle className="icon-error" />
            )}
          </div>
          <div className="login-toast-content">
            <h4>{notification.title}</h4>
            <p>{notification.message}</p>
          </div>
          {notification.type !== "success" && (
            <button
              type="button"
              className="login-toast-close"
              onClick={() => setNotification(null)}
              aria-label="Close notification"
            >
              ×
            </button>
          )}
        </div>
      )}

      <div className="login-card">
        {/* ================= MANDIR BRANDING BADGE ================= */}
        <div className="login-badge-wrap">
          <div className="login-badge">
            <FaUserShield className="login-badge-icon" />
          </div>
        </div>

        <h1>Admin Portal</h1>
        <p className="login-subtitle">Sri Gnana Sai Baba Mandir</p>

        <form onSubmit={handleLogin} noValidate>
          {/* ================= EMAIL INPUT ================= */}
          <div className="login-input-group">
            <FaEnvelope className="input-icon" />
            <input
              type="email"
              placeholder="Admin Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              autoComplete="email"
              required
            />
          </div>

          {/* ================= PASSWORD INPUT ================= */}
          <div className="login-input-group">
            <FaLock className="input-icon" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              className="toggle-pwd-btn"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              tabIndex="-1"
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {/* ================= SUBMIT BUTTON ================= */}
          <button
            type="submit"
            className="login-submit-btn"
            disabled={loading}
          >
            {loading ? (
              <span className="login-btn-loading">
                <FaSpinner className="spinner-icon" /> Signing in...
              </span>
            ) : (
              "Sign In to Dashboard"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
